class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // Toggle is-empty class based on item count or items array
    const itemCount = parsedState.item_count !== undefined ? parsedState.item_count : (parsedState.items ? parsedState.items.length : null);
    const isEmpty = itemCount === 0;
    this.classList.toggle('is-empty', isEmpty);

    // Preserve the wave element before updating
    const drawerInner = this.querySelector('.drawer__inner');
    const waveElement = drawerInner ? drawerInner.querySelector('.drawer__wave-side') : null;

    // If sections are missing OR item count is unknown, fetch the cart and update manually
    if (!parsedState.sections || itemCount === null) {
      fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`)
        .then((response) => response.json())
        .then((data) => {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);

            if (!sectionElement) return;
            if (!data[section.id]) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(data[section.id], section.selector);
          });

          // Re-add wave element after update
          if (waveElement && drawerInner) {
            const existingWave = drawerInner.querySelector('.drawer__wave-side');
            if (!existingWave) {
              drawerInner.insertBefore(waveElement, drawerInner.firstChild);
            }
          }

          // Update is-empty class based on fetched content
          const fetchedCartItems = drawerInner ? drawerInner.querySelectorAll('.cart-item') : [];
          const isFetchedEmpty = fetchedCartItems.length === 0;
          this.classList.toggle('is-empty', isFetchedEmpty);

          // Reinitialize checkout drawer flow after content update
          if (window.checkoutDrawerFlow) {
            window.checkoutDrawerFlow.reinit();
          }
        });
    } else {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        if (!parsedState.sections[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      // Re-add wave element after update
      if (waveElement && drawerInner) {
        const existingWave = drawerInner.querySelector('.drawer__wave-side');
        if (!existingWave) {
          drawerInner.insertBefore(waveElement, drawerInner.firstChild);
        }
      }

      // Reinitialize checkout drawer flow after content update
      if (window.checkoutDrawerFlow) {
        window.checkoutDrawerFlow.reinit();
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
